import React, { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { renderPreviewQuestion } from '@/utils/preview/renderPreviewQuestion';
import { FrontendThemeProvider } from '@/components/preview/FrontendThemeProvider';
import type { PreviewQuestionData, OptionData } from '@/utils/preview/types';

interface PreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  questionType: string;
  questionText: string;
  questionSubtext?: string;
  questionImage?: string | null;
  questionPoints?: number;
  options: OptionData[];
  language?: 'en' | 'fr';
  questionMetadata?: PreviewQuestionData['questionMetadata'];
}

const PreviewModal: React.FC<PreviewModalProps> = ({
  open,
  onOpenChange,
  questionType,
  questionText,
  questionSubtext,
  questionImage,
  questionPoints,
  options,
  language = 'en',
  questionMetadata,
}) => {
  // Use question points directly (not sum of option points)
  const totalPoints = useMemo(() => {
    return questionPoints || 0;
  }, [questionPoints]);

  // Build preview data for renderer
  const previewData: PreviewQuestionData = useMemo(
    () => ({
      questionType,
      questionText,
      questionSubtext,
      questionImage,
      options,
      language,
      totalPoints,
      questionMetadata,
    }),
    [
      questionType,
      questionText,
      questionSubtext,
      questionImage,
      options,
      language,
      totalPoints,
      questionMetadata,
    ]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Question Preview
            <Badge variant="outline">{questionType}</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[600px]">
          {/* Frontend theme provider - applies exact CSS variables from frontend */}
          <FrontendThemeProvider>{renderPreviewQuestion(previewData)}</FrontendThemeProvider>
        </ScrollArea>

        <div className="text-sm text-muted-foreground text-center mt-2">
          This preview shows how the question will appear to users
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewModal;
